import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, XCircle, ChevronDown, ChevronUp, Crown, TrendingUp } from 'lucide-react';
import { useOrganization } from '../../hooks/useOrganization';

export const UsageLimitBanner: React.FC = () => {
    const { data: organization } = useOrganization();
    const [isExpanded, setIsExpanded] = useState(false);

    if (!organization?.usage) return null;

    const { usage, limits, upgrade } = organization;
    const usagePercentage = usage.usage_percentage || 0;
    const warningLevel = usage.warning_level;

    // Only show if usage >= 75%
    if (usagePercentage < 75) return null;

    const isExceeded = warningLevel === 'exceeded' || usagePercentage >= 100;
    const isCritical = warningLevel === 'critical' || usagePercentage >= 90;

    // Determine colors and messages
    const getConfig = () => {
        if (isExceeded) {
            return {
                bg: 'bg-red-50 border-red-500',
                icon: <XCircle className="w-5 h-5 text-red-600" />,
                textColor: 'text-red-800',
                subTextColor: 'text-red-700',
                borderColor: 'border-red-200',
                title: '🚨 Payment Limit Exceeded!',
                message: `You've used ${usage.payments_used.toLocaleString()} of ${limits.payment_flow.toLocaleString()} payments (${usagePercentage.toFixed(1)}%). New payments are NOT being tracked!`,
                ctaText: 'Upgrade Now to Resume Tracking',
            };
        } else if (isCritical) {
            return {
                bg: 'bg-orange-50 border-orange-500',
                icon: <AlertTriangle className="w-5 h-5 text-orange-600" />,
                textColor: 'text-orange-800',
                subTextColor: 'text-orange-700',
                borderColor: 'border-orange-200',
                title: '⚠️ Approaching Payment Limit',
                message: `You've used ${usage.payments_used.toLocaleString()} of ${limits.payment_flow.toLocaleString()} payments (${usagePercentage.toFixed(1)}%). You're about to reach your limit!`,
                ctaText: 'Upgrade Before Limit',
            };
        } else {
            return {
                bg: 'bg-yellow-50 border-yellow-400',
                icon: <TrendingUp className="w-5 h-5 text-yellow-600" />,
                textColor: 'text-yellow-800',
                subTextColor: 'text-yellow-700',
                borderColor: 'border-yellow-200',
                title: '📊 High Usage Alert',
                message: `You've used ${usage.payments_used.toLocaleString()} of ${limits.payment_flow.toLocaleString()} payments (${usagePercentage.toFixed(1)}%). Consider upgrading soon.`,
                ctaText: 'View Plans',
            };
        }
    };

    const config = getConfig();

    return (
        <div className={`border-l-4 rounded-r-lg mb-6 ${config.bg}`}>
            {/* Collapsed Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full p-3 flex items-center justify-between hover:bg-opacity-80 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">{config.icon}</div>
                    <div className="text-left">
                        <p className={`text-sm font-semibold ${config.textColor}`}>
                            {config.title}
                        </p>
                        <p className={`text-xs ${config.subTextColor} mt-0.5`}>
                            {usage.payments_used.toLocaleString()} / {limits.payment_flow.toLocaleString()} payments used ({usagePercentage.toFixed(1)}%)
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {upgrade.available && (
                        <Link
                            to="/pricing"
                            onClick={(e) => e.stopPropagation()}
                            className={`hidden sm:inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium ${isExceeded
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-orange-600 hover:bg-orange-700'
                                } text-white transition-colors`}
                        >
                            <Crown className="w-3 h-3" />
                            Upgrade
                        </Link>
                    )}
                    <div className="flex-shrink-0">
                        {isExpanded ? (
                            <ChevronUp className={`w-5 h-5 ${isExceeded ? 'text-red-600' : isCritical ? 'text-orange-600' : 'text-yellow-600'}`} />
                        ) : (
                            <ChevronDown className={`w-5 h-5 ${isExceeded ? 'text-red-600' : isCritical ? 'text-orange-600' : 'text-yellow-600'}`} />
                        )}
                    </div>
                </div>
            </button>

            {/* Expanded Content */}
            {isExpanded && (
                <div className={`px-3 pb-4 border-t ${config.borderColor}`}>
                    <div className="pt-3 space-y-3">
                        <p className={`text-sm ${config.textColor}`}>
                            {config.message}
                        </p>

                        {isExceeded && (
                            <div className={`p-3 rounded-lg bg-red-100 border border-red-300`}>
                                <p className={`text-sm font-semibold ${config.textColor} mb-1`}>
                                    ⛔ System is Rejecting New Payments
                                </p>
                                <p className={`text-xs ${config.subTextColor}`}>
                                    Any new payment attempts will NOT be tracked or analyzed until you upgrade your plan.
                                </p>
                            </div>
                        )}

                        {/* Progress Bar */}
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <span className={`text-xs font-medium ${config.subTextColor}`}>Usage Progress</span>
                                <span className={`text-xs font-bold ${config.textColor}`}>{usagePercentage.toFixed(1)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${isExceeded ? 'bg-red-600' : isCritical ? 'bg-orange-500' : 'bg-yellow-500'
                                        }`}
                                    style={{ width: `${Math.min(100, usagePercentage)}%` }}
                                />
                            </div>
                        </div>

                        {/* Upgrade Info */}
                        {upgrade.available && upgrade.next_plan && (
                            <div className={`p-3 rounded-lg bg-white border ${config.borderColor}`}>
                                <p className={`text-sm font-semibold ${config.textColor} mb-2`}>
                                    💡 Upgrade to {upgrade.next_plan_name}
                                </p>
                                <ul className={`text-xs space-y-1 ${config.subTextColor}`}>
                                    <li>✓ {upgrade.next_plan_limit?.toLocaleString()} payments/month</li>
                                    <li>✓ More team members & features</li>
                                    <li>✓ Extended data retention</li>
                                </ul>
                                <Link
                                    to="/pricing"
                                    className={`inline-flex items-center gap-1 mt-2 text-sm font-medium ${isExceeded ? 'text-red-900' : 'text-orange-900'
                                        } hover:underline`}
                                >
                                    <Crown className="w-4 h-4" />
                                    {config.ctaText} →
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
